import { Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { ArrowRight, Loader2, Mail, Phone } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { SectionHeading } from "@/components/site/Blocks";
import { sendContactMessage } from "@/lib/contact.functions";
import type { MediaInquiryCopy, PressContact } from "@/lib/media-inquiries";
import { FormPrivacyNotice, RequiredFieldHint } from "@/components/site/FormPrivacyNotice";

const inputClass =
  "mt-2 w-full rounded-md border border-input bg-background px-4 py-3 text-sm outline-none focus:border-ring focus:ring-2 focus:ring-ring/30";

interface MediaInquiryProps {
  copy: MediaInquiryCopy;
  locale: "en" | "tr";
  contacts: PressContact[];
}

export function MediaInquirySections({ copy, locale, contacts }: MediaInquiryProps) {
  const isTr = locale === "tr";
  const [form, setForm] = useState({
    name: "",
    outlet: "",
    email: "",
    topic: copy.topics[0]!,
    deadline: "",
    message: "",
  });
  const [honeypot, setHoneypot] = useState("");
  const [startedAt] = useState(() => Date.now());
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState<{ kind: "ok" | "error"; text: string } | null>(null);
  const send = useServerFn(sendContactMessage);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSending(true);
    try {
      const deadlineLine = form.deadline.trim()
        ? `${copy.fields.deadline}: ${form.deadline.trim()}\n`
        : "";
      const result = await send({
        data: {
          name: form.name,
          company: form.outlet,
          email: form.email,
          topic: `Media inquiry — ${form.topic}`,
          message: `${copy.fields.outlet}: ${form.outlet || "—"}\n${deadlineLine}\n${form.message}`,
          honeypot,
          startedAt,
          formId: `media-inquiries-${locale}`,
        },
      });

      if (result?.ok) {
        toast.success(copy.successToast);
        setForm({ ...form, name: "", outlet: "", email: "", deadline: "", message: "" });
        setStatus({ kind: "ok", text: copy.successText });
      } else {
        const text = result?.message ?? copy.errorText;
        toast.error(text);
        setStatus({ kind: "error", text });
      }
    } catch (error) {
      console.error("[media-inquiries] submit failed:", error);
      toast.error(copy.errorText);
      setStatus({ kind: "error", text: copy.errorText });
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      <section className="section-shell grid gap-12 py-16 md:py-20 lg:grid-cols-[1.1fr_0.9fr]">
        <form
          className="rounded-lg border border-border bg-card p-8 shadow-card"
          onSubmit={handleSubmit}
          aria-labelledby="media-form-heading"
        >
          <h2 id="media-form-heading" className="font-display text-xl font-bold">
            {copy.formHeading}
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">{copy.formLead}</p>
          <RequiredFieldHint locale={locale} />

          <div className="mt-6 grid gap-5 sm:grid-cols-2">
            <label className="block text-sm font-semibold">
              {copy.fields.name} <span className="text-destructive">*</span>
              <input
                required
                minLength={2}
                className={inputClass}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </label>
            <label className="block text-sm font-semibold">
              {copy.fields.outlet} <span className="text-destructive">*</span>
              <input
                required
                className={inputClass}
                value={form.outlet}
                onChange={(e) => setForm({ ...form, outlet: e.target.value })}
              />
            </label>
          </div>

          <label className="mt-5 block text-sm font-semibold">
            {copy.fields.email} <span className="text-destructive">*</span>
            <input
              required
              type="email"
              className={inputClass}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </label>

          <label className="mt-5 block text-sm font-semibold">
            {copy.fields.topic}
            <select
              className={inputClass}
              value={form.topic}
              onChange={(e) => setForm({ ...form, topic: e.target.value })}
            >
              {copy.topics.map((topic) => (
                <option key={topic}>{topic}</option>
              ))}
            </select>
          </label>

          <label className="mt-5 block text-sm font-semibold">
            {copy.fields.deadline}
            <input
              className={inputClass}
              placeholder={copy.fields.deadlineHint}
              value={form.deadline}
              onChange={(e) => setForm({ ...form, deadline: e.target.value })}
            />
          </label>

          <label className="mt-5 block text-sm font-semibold">
            {copy.fields.message} <span className="text-destructive">*</span>
            <textarea
              required
              minLength={10}
              rows={6}
              className={inputClass}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
            />
          </label>

          {/* Honeypot — hidden from real users, bots tend to fill it in */}
          <div aria-hidden="true" className="absolute left-[-9999px] h-0 w-0 overflow-hidden">
            <label>
              Website
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
              />
            </label>
          </div>

          <FormPrivacyNotice locale={locale} />

          <button
            type="submit"
            disabled={sending}
            className="mt-7 inline-flex items-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-bold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-60"
          >
            {sending ? copy.submitting : copy.submit}
            {sending ? (
              <Loader2 className="size-4 animate-spin" aria-hidden="true" />
            ) : (
              <ArrowRight className="size-4" aria-hidden="true" />
            )}
          </button>

          <p aria-live="polite" className="sr-only">
            {status?.text ?? ""}
          </p>
          {status && (
            <p
              className={`mt-4 text-sm ${status.kind === "ok" ? "text-primary" : "text-destructive"}`}
            >
              {status.text}
            </p>
          )}

          <p className="mt-6 text-xs text-muted-foreground">
            {copy.privacyNote}{" "}
            {isTr ? (
              <Link to="/tr/gizlilik" className="text-primary underline-offset-4 hover:underline">
                Gizlilik
              </Link>
            ) : (
              <Link to="/privacy" className="text-primary underline-offset-4 hover:underline">
                Privacy
              </Link>
            )}
          </p>
        </form>

        <div className="rounded-xl border border-border bg-card p-8 shadow-card">
          <h2 className="font-display text-xl font-bold">{copy.responseHeading}</h2>
          <ul className="mt-4 space-y-3 text-sm leading-relaxed text-muted-foreground">
            {copy.responseItems.map((item) => (
              <li key={item.slice(0, 30)}>{item}</li>
            ))}
          </ul>
          {isTr ? (
            <Link
              to="/tr/basin"
              className="mt-6 inline-flex items-center gap-2 font-semibold text-primary underline-offset-4 hover:underline"
            >
              {copy.pressKitLabel} <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
          ) : (
            <Link
              to="/press"
              className="mt-6 inline-flex items-center gap-2 font-semibold text-primary underline-offset-4 hover:underline"
            >
              {copy.pressKitLabel} <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
          )}
        </div>
      </section>

      <section className="bg-secondary" aria-labelledby="media-contacts">
        <div className="section-shell py-16 md:py-20">
          <SectionHeading
            eyebrow={copy.eyebrow}
            title={copy.contactsHeading}
            lead={copy.contactsLead}
            titleId="media-contacts"
          />
          <ul className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {contacts.map((contact) => (
              <li key={contact.name} className="h-full">
                <article className="flex h-full flex-col rounded-xl border border-border bg-card p-6 shadow-card">
                  <h3 className="font-display text-lg font-bold">{contact.name}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{contact.role}</p>
                  <dl className="mt-4 space-y-2 text-sm">
                    <div>
                      <dt className="font-semibold">{copy.contactLabels.topics}</dt>
                      <dd className="text-muted-foreground">{contact.topics}</dd>
                    </div>
                    <div>
                      <dt className="font-semibold">{copy.contactLabels.language}</dt>
                      <dd className="text-muted-foreground">{contact.language}</dd>
                    </div>
                  </dl>
                  <p className="mt-4 flex items-center gap-2 text-sm">
                    <Mail className="size-4 text-primary" aria-hidden="true" />
                    <a
                      href={`mailto:${contact.email}`}
                      className="font-semibold text-primary underline-offset-4 hover:underline"
                    >
                      {contact.email}
                    </a>
                  </p>
                  {contact.phone && contact.phoneHref && (
                    <p className="mt-1 flex items-center gap-2 text-sm">
                      <Phone className="size-4 text-primary" aria-hidden="true" />
                      <a
                        href={contact.phoneHref}
                        className="font-semibold text-primary underline-offset-4 hover:underline"
                      >
                        {contact.phone}
                      </a>
                    </p>
                  )}
                </article>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </>
  );
}
